import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { api, EVENT_LABEL } from "../lib/api";
import { fmtTime } from "../lib/format";
import { Spinner, EmptyState, Button } from "../components/ui";
import { Icon } from "../components/icons";
import { useAuth } from "../auth";
import { canSeeAvailability } from "../lib/permissions";
import { PresenceCard } from "../components/Presence";
import { useUnavailability, DayUnavailable, MyUnavailability, dayIso } from "../components/Unavailability";
import { StudySetlistButton } from "../components/StudySetlist";
import { exportCalendarPdf } from "../lib/pdf";

const WD = ["S", "T", "Q", "Q", "S", "S", "D"]; // Seg..Dom
// Same palette as the Mobile app calendar (src/theme.ts + app/calendar.tsx).
const KIND_COLOR = { ensaio: "var(--brand-primary)", concerto: "var(--on-surface)", outro: "var(--muted)" };

function monthKey(d) {
  return `${d.getFullYear()}-${d.getMonth()}`;
}

function EventCard({ e, showAvail, navigate }) {
  return (
    <div className="card" style={{ padding: 16, display: "flex", flexDirection: "column", gap: 8 }} data-testid={`cal-event-${e.id}`}>
      <Link to={`/events/${e.id}`} style={{ display: "flex", flexDirection: "column", gap: 8, color: "inherit", textDecoration: "none" }} data-testid={`cal-event-open-${e.id}`}>
        <div className="row-between">
          <span className="pill doing">{(EVENT_LABEL[e.type || e.kind] || "Evento").toUpperCase()}</span>
          <span style={{ color: "var(--on-surface-3)", fontSize: 14 }}>{fmtTime(e.starts_at)}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ fontFamily: "var(--f-medium)", fontSize: 17, flex: 1 }}>{e.title}</div>
          <Icon name="chevron-forward" size={18} color="var(--muted)" />
        </div>
        {e.location ? <div style={{ color: "var(--on-surface-3)", fontSize: 14, display: "flex", gap: 6, alignItems: "center" }}><Icon name="location-outline" size={15} />{[e.location, e.address].filter(Boolean).join(" · ")}</div> : null}
        {e.songs?.length ? <div className="t-hint">Alinhamento: {e.songs.map((x) => x.title).join(" · ")}</div> : null}
      </Link>
      <PresenceCard event={e} compact />
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <StudySetlistButton event={e} testid={`calendar-study-${e.id}`} />
        {showAvail ? <Button className="auto sm ghost" icon="people" onClick={() => navigate(`/events/${e.id}/disponibilidade`)} data-testid={`calendar-availability-${e.id}`}>DISPONIBILIDADE</Button> : null}
      </div>
    </div>
  );
}

export default function Calendar() {
  const { user } = useAuth();
  const showAvail = canSeeAvailability(user);
  const navigate = useNavigate();
  const [cursor, setCursor] = useState(() => new Date());
  const [selected, setSelected] = useState(null);
  const y = cursor.getFullYear();
  const m = cursor.getMonth();

  const q = useQuery({
    queryKey: ["events", y, m],
    queryFn: () =>
      api(`/events?start=${encodeURIComponent(new Date(y, m, 1).toISOString())}&end=${encodeURIComponent(new Date(y, m + 1, 1).toISOString())}`),
  });
  const events = q.data || [];

  const byDay = {};
  events.forEach((e) => {
    const d = new Date(e.starts_at).getDate();
    (byDay[d] = byDay[d] || []).push(e);
  });

  const first = new Date(y, m, 1);
  const startOffset = (first.getDay() + 6) % 7; // Monday-based
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const monthRaw = cursor.toLocaleDateString("pt-PT", { month: "long", year: "numeric" });
  const monthLabel = monthRaw.charAt(0).toUpperCase() + monthRaw.slice(1);
  const dayEvents = selected ? byDay[selected] || [] : [];
  const unav = useUnavailability(user, y, m);
  const sorted = [...events].sort((a, b) => a.starts_at.localeCompare(b.starts_at));

  return (
    <>
      <div className="page-header" style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <h1 className="t-h1" style={{ flex: 1 }}>Calendário</h1>
        <Button className="auto sm ghost" icon="download" disabled={!events.length} onClick={() => exportCalendarPdf([...events].sort((a, b) => a.starts_at.localeCompare(b.starts_at)), monthLabel, (t) => EVENT_LABEL[t] || "Evento")} data-testid="calendar-pdf">PDF</Button>
      </div>
      <div className="page ulc-fade" style={{ maxWidth: 820 }}>
        <div className="row-between" style={{ marginBottom: 16 }}>
          <button className="icon-btn" aria-label="Mês anterior" onClick={() => { setCursor(new Date(y, m - 1, 1)); setSelected(null); }} data-testid="cal-prev"><Icon name="chevron-back" size={20} /></button>
          <div style={{ fontFamily: "var(--f-medium)", fontSize: 18 }} data-testid="cal-month">{monthLabel}</div>
          <button className="icon-btn" aria-label="Mês seguinte" onClick={() => { setCursor(new Date(y, m + 1, 1)); setSelected(null); }} data-testid="cal-next"><Icon name="chevron-forward" size={20} /></button>
        </div>

        {q.isLoading ? (
          <Spinner />
        ) : (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 6 }}>
              {WD.map((w, i) => (
                <div key={`wd-${i}`} style={{ textAlign: "center", color: "var(--muted)", fontFamily: "var(--f-medium)", fontSize: 11, paddingBottom: 4 }}>{w}</div>
              ))}
              {cells.map((d, i) => {
                const evs = d ? byDay[d] || [] : [];
                const active = !!d && d === selected;
                const now = new Date();
                const isToday = d && now.getFullYear() === y && now.getMonth() === m && now.getDate() === d;
                const unavailDay = d && unav.on(dayIso(y, m, d)).length > 0;
                return (
                  <button
                    key={d || `pad-${i}`}
                    disabled={!d}
                    onClick={() => setSelected(d)}
                    data-testid={d ? `cal-day-${d}` : undefined}
                    style={{
                      width: 40, height: 40, justifySelf: "center", borderRadius: "var(--r-pill)", border: `1px solid ${active || isToday ? "var(--brand-primary)" : "transparent"}`,
                      background: active ? "var(--brand-primary)" : unavailDay ? "var(--warning)" : "transparent", opacity: unavailDay && !active ? 0.85 : 1,
                      color: "var(--on-surface)", cursor: d ? "pointer" : "default", display: "flex", flexDirection: "column",
                      alignItems: "center", justifyContent: "center", gap: 2, position: "relative", padding: 0,
                    }}
                  >
                    {d ? <span style={{ fontFamily: "var(--f-regular)", fontSize: 14 }}>{d}</span> : null}
                    {unavailDay ? <span className="sr-only" data-testid={`cal-day-unavail-${d}`}>Indisponibilidade</span> : null}
                    {evs.length ? (
                      <span style={{ display: "flex", gap: 3 }}>
                        {evs.slice(0, 3).map((e) => (
                          <span key={e.id} style={{ width: 6, height: 6, borderRadius: 999, background: active ? "var(--on-brand)" : KIND_COLOR[e.type || e.kind] || "var(--muted)" }} />
                        ))}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>

            <div style={{ display: "flex", gap: 16, margin: "16px 0", flexWrap: "wrap" }}>
              {Object.entries(EVENT_LABEL).map(([k, label]) => (
                <span key={k} style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--muted)", fontSize: 12 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 999, background: KIND_COLOR[k] }} /> {label}
                </span>
              ))}
            </div>

            {selected ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }} data-testid="cal-day-detail">
                <div className="row-between">
                  <span className="t-section">Dia {selected}</span>
                  <button className="chip" onClick={() => setSelected(null)} data-testid="cal-show-month">VER O MÊS</button>
                </div>
                <DayUnavailable items={unav.on(dayIso(y, m, selected))} all={unav.all} user={user} />
                {dayEvents.length ? (
                  dayEvents.map((e) => (
                    <EventCard key={e.id} e={e} showAvail={showAvail} navigate={navigate} />
                  ))
                ) : (
                  <EmptyState icon="calendar-outline" text="Sem compromissos neste dia." />
                )}
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }} data-testid="cal-month-list">
                <div className="t-section">Neste mês</div>
                {sorted.length ? sorted.map((e) => (
                  <div key={e.id}>
                    <div className="t-hint" style={{ marginBottom: 4 }}>{new Date(e.starts_at).toLocaleDateString("pt-PT", { weekday: "long", day: "numeric", month: "long" })}</div>
                    <EventCard e={e} showAvail={showAvail} navigate={navigate} />
                  </div>
                )) : <EmptyState icon="calendar-outline" text="Sem compromissos este mês." testid="calendar-month-empty" />}
              </div>
            )}
            <MyUnavailability user={user} initialDay={selected ? dayIso(y, m, selected) : undefined} />
          </>
        )}
      </div>
    </>
  );
}
